import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { orderBy } from 'utils/arrayUtils';
import { typedEntries } from 'utils/funcs';
import { TriggerAddEditModalFormValues } from './TriggerAddEditModal.schema';

/**
 * This is the initialization order we should fill in the form fields, to avoid validation issues when rendering due to
 * dependent fields.
 */
const INITIALIZATION_FIELD_ORDER: (keyof TriggerAddEditModalFormValues)[] = [
  'name',
  'triggerType',
  'eventType',
  'conditions',
  'timeCondition',
  'actionType',
  'recipientsList',
  'templateUid',
  'serviceUid',
  'jobTiming',
];

/**
 * Initializes the form as dirty giving the default values it should use.
 */
const useTriggerAddEditModalInitializeAsDirty = ({
  initializeWith,
}: {
  initializeWith: TriggerAddEditModalFormValues | undefined;
}) => {
  const hasRun = useRef(false);

  const { setValue } = useFormContext<TriggerAddEditModalFormValues>();

  useEffect(() => {
    if (!initializeWith || hasRun.current) {
      return () => {};
    }

    // Needs a little timeout in order to `isDirty` properly works, probably this can be changed to check by the
    // formState `isReady` property once we upgrade react-hook-form.
    // Ref: https://github.com/orgs/react-hook-form/discussions/9913
    const timeout = setTimeout(() => {
      const fields = orderBy(typedEntries(initializeWith), [
        {
          field: ([key]) => INITIALIZATION_FIELD_ORDER.indexOf(key),
          order: 'asc',
        },
      ]);

      fields.forEach(([key, value]) => {
        setValue(key, value, { shouldDirty: true, shouldValidate: true });
      });

      hasRun.current = true;
    });

    return () => {
      clearTimeout(timeout);
    };
  }, [initializeWith, setValue, initializeWith]);
};

export default useTriggerAddEditModalInitializeAsDirty;
